/**
 * UE5 "Manny" (SK_Mannequin) rest skeleton — 89 bones, extracted from the node
 * hierarchy embedded in public/anim/manny/idle.glb (the TRUE clip-neutral rest, perfectly
 * L/R symmetric) via client/test/extract-manny.mts. Positions are LOCAL-to-parent in METRES
 * (source cm * 0.01); quaternions [x,y,z,w]. The root carries a Z-up -> Y-up conversion + a
 * foot-to-ground offset so the rig stands feet-at-origin. The male body fit (build-male.mts),
 * the Mixamo retarget target, and avatar.ts TWIST_BIND all derive from this table; Manny
 * clips bind by bone name (no retarget). Generated — do not hand-edit; re-run the extractor
 * (then `npm run build:male`).
 *
 * ⚠ v1 of this table was a posed flight-idle FBX snapshot (asymmetric, wrists rolled
 * +82°L/−34°R off clip-neutral) — the root cause of permanently deformed/asymmetric
 * forearms. Do not regenerate from FBX node TRS.
 *
 * Tuple: [name, parentIndex, px, py, pz, qx, qy, qz, qw]
 */
export type MannyBoneDef = [string, number, number, number, number, number, number, number, number];

export const MANNY_RIG: readonly MannyBoneDef[] = [
  ["root", -1, 0, -0.007508, 0, -0.707107, 0, 0, 0.707107],
  ["pelvis", 0, 0, -0.022809, 0.958968, -0.022415, -0.706751, 0.022415, 0.706751],
  ["spine_01", 1, 0.036771, 0, 0, 0, 0, -0.12583, 0.992052],
  ["spine_02", 2, 0.067951, 0, 0, 0, 0, 0.030229, 0.999543],
  ["spine_03", 3, 0.072382, 0, 0, 0, 0, 0.095377, 0.995441],
  ["spine_04", 4, 0.085239, 0, 0, 0.000004, 0, 0.051177, 0.99869],
  ["spine_05", 5, 0.194398, 0, 0, -0.000004, 0, 0.005946, 0.999982],
  ["neck_01", 6, 0.118878, 0, 0, 0, 0, -0.207301, 0.978277],
  ["neck_02", 7, 0.051103, 0, 0, -0.000001, 0, 0.016698, 0.999861],
  ["head", 8, 0.04913, 0, 0, 0.000001, 0, 0.103488, 0.994631],
  ["clavicle_l", 6, 0.055163, 0.013148, -0.014279, 0.083278, 0.75456, -0.04129, 0.649614],
  ["upperarm_l", 10, 0.178095, 0, 0, -0.019948, 0.391731, -0.02019, 0.919642],
  ["lowerarm_l", 11, 0.277711, 0, 0, 0, 0, -0.333633, 0.942703],
  ["lowerarm_twist_02_l", 12, 0.090837, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["lowerarm_twist_01_l", 12, 0.181674, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["hand_l", 12, 0.272511, 0, 0, -0.557587, 0.001678, 0.020561, 0.829862],
  ["middle_metacarpal_l", 15, 0.033758, -0.007536, -0.001829, -0.037291, -0.000386, -0.020259, 0.999099],
  ["middle_01_l", 16, 0.060982, 0, 0, 0, 0, 0.272051, 0.962283],
  ["middle_02_l", 17, 0.05169, 0, 0, 0, 0, 0.180255, 0.98362],
  ["middle_03_l", 18, 0.02474, 0, 0, 0, 0, 0.087156, 0.996195],
  ["pinky_metacarpal_l", 15, 0.033144, -0.003059, 0.023911, -0.218231, -0.188166, 0.058286, 0.955809],
  ["pinky_01_l", 20, 0.049576, -0.001431, -0.001988, 0.089984, 0.017016, 0.128066, 0.987528],
  ["pinky_02_l", 21, 0.03816, 0, 0, 0, 0, 0.184698, 0.982795],
  ["pinky_03_l", 22, 0.0204, 0, 0, 0, 0, 0.042896, 0.99908],
  ["ring_metacarpal_l", 15, 0.033743, -0.005425, 0.010918, -0.116599, -0.100569, -0.02566, 0.987741],
  ["ring_01_l", 24, 0.056455, -0.000416, -0.000207, 0.054216, 0.013177, 0.25354, 0.965715],
  ["ring_02_l", 25, 0.04977, 0, 0, 0, 0, 0.164738, 0.986337],
  ["ring_03_l", 26, 0.02265, 0, 0, 0, 0, 0.079921, 0.996801],
  ["thumb_01_l", 15, 0.019924, 0.013566, -0.025815, 0.505193, 0.36913, -0.067039, 0.777193],
  ["thumb_02_l", 28, 0.04378, 0, 0, 0.033565, -0.010302, 0.201856, 0.978786],
  ["thumb_03_l", 29, 0.03086, 0, 0, 0, 0, 0.087156, 0.996195],
  ["index_metacarpal_l", 15, 0.034445, -0.003847, -0.023793, 0.028966, 0.063705, -0.007109, 0.997523],
  ["index_01_l", 31, 0.058771, 0.000432, 0.002409, 0, 0, 0.202557, 0.979271],
  ["index_02_l", 32, 0.0408, 0, 0, 0, 0, 0.129597, 0.991567],
  ["index_03_l", 33, 0.02595, 0, 0, 0, 0, 0.109009, 0.994041],
  ["upperarm_twist_01_l", 11, 0.09257, 0, 0, -0.000054, -0.01879, -0.00285, 0.999819],
  ["upperarm_twist_02_l", 11, 0.185141, 0, 0, 0, 0, 0, 1],
  ["clavicle_r", 6, 0.055162, 0.013148, 0.014279, 0.75456, -0.083278, -0.649614, -0.04129],
  ["upperarm_r", 37, -0.178096, 0, 0.000004, -0.019948, 0.391731, -0.02019, 0.919642],
  ["lowerarm_r", 38, -0.277707, 0, 0, 0, 0, -0.333633, 0.942703],
  ["lowerarm_twist_02_r", 39, -0.090837, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["lowerarm_twist_01_r", 39, -0.181673, 0, 0, 0.011478, -0.014877, 0.017495, 0.99967],
  ["hand_r", 39, -0.27251, 0, 0, -0.557587, 0.001678, 0.020561, 0.829862],
  ["middle_metacarpal_r", 42, -0.033758, 0.00754, 0.001828, -0.037291, -0.000386, -0.020259, 0.999099],
  ["middle_01_r", 43, -0.060984, -0.000001, 0, 0, 0, 0.272051, 0.962283],
  ["middle_02_r", 44, -0.05169, -0.000001, 0, 0, 0, 0.180255, 0.98362],
  ["middle_03_r", 45, -0.02474, 0, 0, 0, 0, 0.087156, 0.996195],
  ["pinky_metacarpal_r", 42, -0.033147, 0.003059, -0.023913, -0.218231, -0.188166, 0.058286, 0.955809],
  ["pinky_01_r", 47, -0.049573, 0.001433, 0.001989, 0.089984, 0.017016, 0.128066, 0.987528],
  ["pinky_02_r", 48, -0.03816, 0, 0, 0, 0, 0.184698, 0.982795],
  ["pinky_03_r", 49, -0.0204, 0, 0, 0, 0, 0.042896, 0.99908],
  ["ring_metacarpal_r", 42, -0.033742, 0.00543, -0.010918, -0.116599, -0.100569, -0.02566, 0.987741],
  ["ring_01_r", 51, -0.056457, 0.000414, 0.000207, 0.054216, 0.013177, 0.25354, 0.965715],
  ["ring_02_r", 52, -0.049771, 0, 0, 0, 0, 0.164738, 0.986337],
  ["ring_03_r", 53, -0.02265, 0.000001, 0, 0, 0, 0.079921, 0.996801],
  ["thumb_01_r", 42, -0.019928, -0.013567, 0.025813, 0.505193, 0.36913, -0.067039, 0.777193],
  ["thumb_02_r", 55, -0.043778, 0.000004, -0.000001, 0.033565, -0.010302, 0.201856, 0.978786],
  ["thumb_03_r", 56, -0.03086, 0, 0, 0, 0, 0.087156, 0.996195],
  ["index_metacarpal_r", 42, -0.034445, 0.003852, 0.023793, 0.028966, 0.063705, -0.007109, 0.997523],
  ["index_01_r", 58, -0.058772, -0.000434, -0.00241, 0, 0, 0.202557, 0.979271],
  ["index_02_r", 59, -0.040799, 0, 0, 0, 0, 0.129597, 0.991567],
  ["index_03_r", 60, -0.025951, 0, 0, 0, 0, 0.109009, 0.994041],
  ["upperarm_twist_01_r", 38, -0.092569, 0, -0.000001, -0.000054, -0.01879, -0.00285, 0.999819],
  ["upperarm_twist_02_r", 38, -0.185138, 0, -0.000002, 0, 0, 0, 1],
  ["thigh_r", 1, -0.023657, -0.001195, 0.099691, 0.029462, 0.072405, 0.996519, 0.028969],
  ["calf_r", 64, 0.433413, 0, 0, 0, 0, -0.043662, 0.999046],
  ["foot_r", 65, 0.422179, 0, 0, -0.000666, 0.026877, 0.023239, 0.999368],
  ["ball_r", 66, 0.070094, 0.152376, -0.005389, 0, 0, -0.707107, 0.707107],
  ["calf_twist_02_r", 65, 0.140726, 0, 0.000377, -0.000093, 0.002251, 0.023249, 0.999727],
  ["calf_twist_01_r", 65, 0.281453, 0, 0.000755, -0.000093, 0.002251, 0.023249, 0.999727],
  ["thigh_twist_01_r", 64, 0.144471, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_twist_02_r", 64, 0.288942, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_l", 1, -0.023657, -0.0011, -0.099692, 0.072405, -0.029462, -0.028969, 0.996519],
  ["calf_l", 72, -0.433413, 0, 0, 0, 0, -0.043662, 0.999046],
  ["foot_l", 73, -0.422179, 0, 0, -0.000666, 0.026877, 0.023239, 0.999368],
  ["ball_l", 74, -0.070094, -0.152376, 0.005389, 0, 0, -0.707107, 0.707107],
  ["calf_twist_02_l", 73, -0.140726, 0, 0, -0.000093, 0.002251, 0.023249, 0.999727],
  ["calf_twist_01_l", 73, -0.281453, 0, -0.000754, -0.000093, 0.002251, 0.023249, 0.999727],
  ["thigh_twist_01_l", 72, -0.144471, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["thigh_twist_02_l", 72, -0.288942, 0, 0, 0.000026, 0.002253, -0.011408, 0.999932],
  ["ik_foot_root", 0, 0, 0, 0, 0, 0, 0, 1],
  ["ik_foot_l", 80, 0.140877, 0.009943, 0.082376, 0.063632, -0.704247, 0.041376, 0.705887],
  ["ik_foot_r", 80, -0.140876, 0.009848, 0.082382, 0.705887, -0.041376, -0.704247, -0.063632],
  ["ik_hand_root", 0, 0, 0, 0, 0, 0, 0, 1],
  ["ik_hand_gun", 83, -0.477683, -0.156988, 1.044664, 0.59846, -0.009649, 0.5206, 0.608875],
  ["ik_hand_l", 84, 0.437336, -0.616698, 0.584081, -0.457766, 0.645509, -0.611366, 0],
  ["ik_hand_r", 84, 0, 0, 0, 0, 0, 0, 1],
  ["interaction", 0, 0, 0, 0, 0, 0, 0, 1],
  ["center_of_mass", 0, 0, 0, 0, 0, 0, 0, 1],
];

export const MANNY_BONE_NAMES: readonly string[] = MANNY_RIG.map((b) => b[0]);
